/**
 * V2 Conformance — path pattern engine
 *
 * Tiny, dependency-free resolver shared by the strict validator and the
 * normalizer so both interpret the SAME rule declarations identically.
 *
 * A "pattern" is a dot path relative to a root object. Segment kinds:
 *   - literal   → descend into that object key
 *   - `key[]`   → descend into `key`, then fan out over every array element
 *                 (matches the mapping-table convention `options.items[].title…`)
 *   - `[]`      → current node must be an array; fan out over every element
 *   - `*`       → current node must be an object; fan out over every value
 *                 (used for `translations.*` locale maps)
 *
 * Resolution returns the matched nodes BY REFERENCE. The normalizer deep-clones
 * its input first and then mutates these references in place — that is why the
 * resolver intentionally does not clone.
 *
 * @module screen-utils/v2-conformance
 */

export type PlainObject = Record<string, unknown>;

export function isPlainObject(value: unknown): value is PlainObject {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

/**
 * Resolve a base pattern to every matching node that is a plain object.
 * Non-object terminals (strings, numbers, arrays) are dropped — hosts are
 * always the objects that carry the canonical/legacy leaves.
 */
export function resolveHosts(root: unknown, pattern: string): PlainObject[] {
  if (!pattern) return isPlainObject(root) ? [root] : [];

  let current: unknown[] = [root];
  for (const seg of pattern.split('.')) {
    const isArray = seg.endsWith('[]');
    const key = isArray ? seg.slice(0, -2) : seg;
    const next: unknown[] = [];
    for (const node of current) {
      if (key === '') {
        // bare `[]`
        if (Array.isArray(node)) next.push(...node);
      } else if (key === '*') {
        if (isPlainObject(node)) {
          for (const v of Object.values(node)) {
            if (isArray) { if (Array.isArray(v)) next.push(...v); } else next.push(v);
          }
        }
      } else if (isPlainObject(node)) {
        const child = node[key];
        if (child === undefined) continue;
        if (isArray) { if (Array.isArray(child)) next.push(...child); } else next.push(child);
      }
    }
    current = next;
  }
  return current.filter(isPlainObject);
}

export interface LeafRef {
  /** Whether the leaf key is present on its (existing) parent object. */
  exists: boolean;
  value: unknown;
  /** The object that directly owns the leaf key (undefined if the path broke). */
  parent: PlainObject | undefined;
  key: string;
}

/**
 * Navigate a dot subpath (no wildcards) under a host and return a handle to the
 * final leaf — its owning parent, key, presence, and current value.
 */
export function getLeaf(host: PlainObject, leaf: string): LeafRef {
  const segs = leaf.split('.');
  const key = segs[segs.length - 1];
  let parent: PlainObject = host;
  for (let i = 0; i < segs.length - 1; i++) {
    const child = parent[segs[i]];
    if (!isPlainObject(child)) {
      return { exists: false, value: undefined, parent: undefined, key };
    }
    parent = child;
  }
  return { exists: key in parent, value: parent[key], parent, key };
}

/** Set a leaf value under a host, creating intermediate objects as needed. */
export function setLeaf(host: PlainObject, leaf: string, value: unknown): void {
  const segs = leaf.split('.');
  let parent: PlainObject = host;
  for (let i = 0; i < segs.length - 1; i++) {
    const seg = segs[i];
    const child = parent[seg];
    if (!isPlainObject(child)) parent[seg] = {};
    parent = parent[seg] as PlainObject;
  }
  parent[segs[segs.length - 1]] = value;
}

/**
 * Delete a leaf under a host and prune any intermediate objects that became
 * empty as a result (so a migrated `lineAppearance.color` removes an emptied
 * `lineAppearance` container rather than leaving `{}` behind).
 */
export function deleteLeaf(host: PlainObject, leaf: string): boolean {
  const segs = leaf.split('.');
  const chain: PlainObject[] = [host];
  let parent: PlainObject = host;
  for (let i = 0; i < segs.length - 1; i++) {
    const child = parent[segs[i]];
    if (!isPlainObject(child)) return false;
    parent = child;
    chain.push(child);
  }
  const key = segs[segs.length - 1];
  if (!(key in parent)) return false;
  delete parent[key];

  // Prune emptied intermediate containers, deepest first (not the host itself).
  for (let i = chain.length - 1; i >= 1; i--) {
    const node = chain[i];
    if (Object.keys(node).length === 0) {
      const ownerKey = segs[i - 1];
      delete chain[i - 1][ownerKey];
    } else {
      break;
    }
  }
  return true;
}
